import {Player} from '@wix/music-player';
import {combineLatest, EMPTY, from, MonoTypeOperatorFunction, Observable, of, throwError} from 'rxjs';
import {catchError, delayWhen, retryWhen, share, switchMap, tap} from 'rxjs/operators';
import {WidgetSettings} from '../../../../typings/settings';
import {WixMusicWidgetModule} from '../../../../typings/wix-music-widget';
import {appLoadingPhaseStart, LoadingPhases} from '../../../logging/fedops';
import {logPlayerStarted} from '../../../logging/logging';
import {setPlayerVersionLogging} from '../../../logging/playerVersion';
import {CollectionResponse} from '../../../services/CollectionManager/ICollectionManager';
import {sendPlayerStartedEvent} from '../../../statistics/bi';
import {logPlayerEvents} from '../../../statistics/send-event';
import {debug} from '../../../utils/debug';
import {writeOops} from '../../../utils/oops';
import {subscribeToPlayerEvents} from '../../subscribe-to-player-events';
import {subscribeOnPlayerEventsToGetWindow} from '../subscribeOnPlayerEventsToGetWindow';
import {documentVisible} from '../visibilityObserver';

interface CreateOptions {
  element: Element;
  Pl$: Observable<WixMusicWidgetModule>;
  setting$: Observable<WidgetSettings>;
  collection$: Observable<CollectionResponse>;
}

function hasMessage(reason: any): reason is {message: string} {
  return typeof reason === 'object' && reason !== null && typeof reason.message === 'string';
}

export function createPlayer({element, Pl$, setting$, collection$}: CreateOptions): Observable<Player> {
  const player$: Observable<Player> = combineLatest([
    Pl$,
    setting$.pipe(debug('init', 'setting received')),
    collection$.pipe(debug('init', 'collection received')),
  ]).pipe(
    switchMap(([Pl, settings, collection]) => {
      appLoadingPhaseStart(LoadingPhases.PlayerInit);

      return of({...collection, ...settings}).pipe(
        debug('init', 'start'),
        switchMap((params) => from(Pl.init(element, params))),
        tap((player) => {
          setPlayerVersionLogging({playerVersion: player.playerVersion});
          subscribeToPlayerEvents(player);
          subscribeOnPlayerEventsToGetWindow(player);
          logPlayerEvents(player.statisticEvents, {sToken: settings.sToken});
          logPlayerStarted();
          setPlayerVersionLogging({playerVersion: player.playerVersion});
        }),
        browserNotSupportedHandler(),
        retryOnInvisibleError(),
        debug('init', 'end'),
      );
    }),
    share(),
  );
  return player$;
}

function browserNotSupportedHandler<T>(): MonoTypeOperatorFunction<T> {
  return catchError((reason) => {
    if (hasMessage(reason) && reason.message === 'Browser Not Supported') {
      writeOops('Your browser is not supported');
      logPlayerStarted();
      sendPlayerStartedEvent();

      return EMPTY;
    }
    return throwError(reason);
  });
}

function retryOnInvisibleError<T>(): MonoTypeOperatorFunction<T> {
  return retryWhen((attempts) =>
    attempts.pipe(
      switchMap((reason) =>
        // we are in firefox and current frame is hidden
        hasMessage(reason) && reason.message.includes('getComputedStyle(...) is null')
          ? of(reason).pipe(delayWhen(() => documentVisible()))
          : throwError(reason),
      ),
    ),
  );
}
